import React from 'react';
import { Pressable } from 'react-native';

import { Stack } from '@/components/layout';
import { Avatar, SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';

import { ActionButton } from './action-button';
import { CARD_HEIGHT } from './constants';
import type { GuardianLessonCarouselItem } from './types';

const strings = STRINGS.guardian.home.lessonCarousel;

type LessonCardProps = {
  item: GuardianLessonCarouselItem;
  width: number;
  onViewDetailsPress?: (item: GuardianLessonCarouselItem) => void;
  onReschedulePress?: (item: GuardianLessonCarouselItem) => void;
  onLocationPress?: (item: GuardianLessonCarouselItem) => void;
};

export function LessonCard({
  item,
  width,
  onViewDetailsPress,
  onReschedulePress,
  onLocationPress,
}: LessonCardProps) {
  const { scale } = useScaling();
  const styles = useStyles();

  return (
    <Stack style={[styles.card, { width }]}>
      <Text
        variant="Font Guardian App/Subtitle/Subtitle 11 Regular"
        style={styles.metaText}
        numberOfLines={1}>
        {item.label} {item.date} · {item.time}
      </Text>

      <Text
        variant="Font Guardian App/Subtitle/Subtitle 14 Bold"
        style={styles.title}
        numberOfLines={1}>
        {item.academyName} · {item.programName}
      </Text>

      <Stack direction="row" align="center" gap={scale(10)} style={styles.coachRow}>
        <Avatar size={scale(20)} borderWidth={0} activeOpacity={1}>
          <Avatar.Image source={item.coachAvatar} backgroundColor={guardianColors['2']} />
        </Avatar>
        <Text
          variant="Font Guardian App/Subtitle/Subtitle 12 Semibold"
          style={styles.coachName}
          numberOfLines={1}>
          {item.coachName}
        </Text>
      </Stack>

      <Stack direction="row" align="center" gap={scale(12)} style={styles.actions}>
        <ActionButton
          label={strings.viewDetails}
          variant="primary"
          onPress={() => onViewDetailsPress?.(item)}
        />
        <ActionButton
          label={strings.reschedule}
          variant="secondary"
          onPress={() => onReschedulePress?.(item)}
        />
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={`Open location for ${item.academyName}`}
          onPress={() => onLocationPress?.(item)}
          style={({ pressed }) => [styles.mapButton, pressed && styles.pressed]}>
          <SvgIcon name="IcMap" size={scale(20)} />
        </Pressable>
      </Stack>
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  card: {
    alignSelf: 'center',
    height: scale(CARD_HEIGHT),
    backgroundColor: colors['0'],
    borderRadius: scale(22),
    borderCurve: 'continuous',
    padding: scale(12),
    shadowColor: '#D5D9E2',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.3,
    shadowRadius: 3,
    elevation: 1,
  },
  metaText: {
    color: guardianColors['6'],
  },
  title: {
    color: guardianColors['10'],
    marginTop: scale(6),
  },
  coachRow: {
    marginTop: scale(8),
  },
  coachName: {
    color: guardianColors['10'],
    flexShrink: 1,
  },
  actions: {
    marginTop: scale(8),
  },
  mapButton: {
    alignItems: 'center',
    justifyContent: 'center',
    width: scale(40),
    height: scale(40),
    borderRadius: scale(12),
    borderCurve: 'continuous',
    padding: scale(10),
    borderWidth: 1,
    borderColor: guardianColors['Main/Main'],
    backgroundColor: colors['0'],
  },
  pressed: {
    opacity: 0.82,
  },
}));
